package dev.lingvo.site.components.studio.compose

/** Encodes opaque canvas RGBA pixels as 8-bit, non-interlaced RGB PNGs. */
object RgbPngEncoder {
    /** Encodes deterministically with portable stored-block zlib. */
    fun encode(width: Int, height: Int, rgba: ByteArray): ByteArray =
        PngChunks.assemble(width, height, StoredDeflate.compressStored(subRows(width, height, rgba)))

    /** Encodes through [deflater], which must return a complete zlib stream. */
    suspend fun encode(width: Int, height: Int, rgba: ByteArray, deflater: Deflate): ByteArray =
        PngChunks.assemble(width, height, deflater.compress(subRows(width, height, rgba)))

    private fun subRows(width: Int, height: Int, rgba: ByteArray): ByteArray {
        require(width > 0 && height > 0) { "PNG dimensions must be positive" }
        val pixels = width.toLong() * height
        require(pixels <= Int.MAX_VALUE / 4 && pixels * 4 == rgba.size.toLong()) {
            "RGBA buffer must contain exactly width × height × 4 bytes"
        }
        val rowSize = width.toLong() * 3 + 1
        require(rowSize * height <= Int.MAX_VALUE) { "PNG scanlines are too large" }
        val rows = ByteArray((rowSize * height).toInt())
        var source = 0
        var target = 0
        repeat(height) {
            rows[target++] = 1 // Sub filter; RGB has three bytes per pixel.
            repeat(width) { x ->
                require(rgba[source + 3].toInt() and 255 == 255) { "RGB PNG requires opaque pixels" }
                repeat(3) { channel ->
                    val left = if (x == 0) 0 else rgba[source - 4 + channel].toInt() and 255
                    rows[target++] = ((rgba[source + channel].toInt() and 255) - left).toByte()
                }
                source += 4
            }
        }
        return rows
    }
}
